import {
  Alert,
  Button,
  Col,
  DatePicker,
  Flex,
  FloatButton,
  Row,
  Segmented,
  Space,
  Tabs,
  Table,
} from "antd";
import { ReloadOutlined } from "@ant-design/icons";
import { useEffect, useState } from "react";
import dayjs from "dayjs";
import "./Dashboard.css";

import { TopBar } from "../../../../components/widgets/top-bar/TopBar";
import { IslandLayout } from "../../../../layouts/island-layout/IslandLayout";
import { useExceptionHandler } from "../../../../hooks/useExceptionHandler";
import { useAlertStore } from "../../../../store/alert-store";
import { useSessionStore } from "../../../../store/session-store";
import { useAxiosPrivate } from "../../../../hooks/useAxiosPrivate";
import { SpinnerLoader } from "../../../../components/widgets/spinner-loader/SpinnerLoader";
import { SupportComponent } from "../support-components/SupportComponent";
import { LLM_WHISPERER_ENDPOINT } from "../../helper";

function Dashboard() {
  const { TabPane } = Tabs;
  const axiosPrivate = useAxiosPrivate();
  const { sessionDetails } = useSessionStore();
  const { setAlertDetails } = useAlertStore();
  const handleException = useExceptionHandler();
  const [loadingUsage, setLoadingUsage] = useState(true);
  const [isLoadingDailyUsage, setLoadingDailyUsage] = useState(true);
  const [dailyUsage, setDailyUsage] = useState([]);
  const [usage, setUsage] = useState([]);
  const [tableData, setTableData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [detailedHistory, setDetailedHistory] = useState([]);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const [toggleValue, setToggleValue] = useState();
  const [datesDetailedHistory, setDatesDetailedHistory] = useState();
  const { RangePicker } = DatePicker;

  useEffect(() => {
    setDetailedHistory([]);
  }, []);

  const rangePresets = [
    { label: "Last 7 Days", value: [dayjs().add(-7, "d"), dayjs()] },
    { label: "Last 14 Days", value: [dayjs().add(-14, "d"), dayjs()] },
    { label: "Last 30 Days", value: [dayjs().add(-30, "d"), dayjs()] },
    { label: "Last 90 Days", value: [dayjs().add(-90, "d"), dayjs()] },
  ];
  const keysEndpoint = `${LLM_WHISPERER_ENDPOINT}/usage/subscription/`;

  const getOverallUsageMetrics = () => {
    setDailyUsage([]);
    const requestOptions = {
      method: "GET",
      url: keysEndpoint,
      headers: {
        "X-CSRFToken": sessionDetails?.csrfToken,
      },
    };
    setLoadingUsage(true);
    axiosPrivate(requestOptions)
      .then((res) => {
        const usage = res?.data;
        if (usage?.usage) {
          setUsage(usage?.usage);
          const portalPlan = usage?.usage?.find(
            (plan) => plan?.product === "LLM Whisperer Pro"
          );
          if (portalPlan) {
            // Set LLM Whisperer Pro Bundle as active
            setToggleValue(portalPlan?.product);
          } else {
            setToggleValue("LLM Whisperer Free");
          }
        }
      })
      .catch((err) => {
        setAlertDetails(handleException(err, "Failed to fetch API Keys"));
      })
      .finally(() => {
        setLoadingUsage(false);
      });
  };
  const detailedUsageEndpoint = `${LLM_WHISPERER_ENDPOINT}/usage/records/`;
  const getDetailedUsage = (page = 1, pageSize = 10) => {
    const subscriptionId = getSubscriptionIdByProduct(toggleValue);
    const startDate = datesDetailedHistory[0].format("YYYY-MM-DD");
    const endDate = datesDetailedHistory[1].format("YYYY-MM-DD");
    const requestOptions = {
      method: "GET",
      url: `${detailedUsageEndpoint}?subscription_id=${subscriptionId}&start_date=${startDate}&end_date=${endDate}&page=${page}&page_size=${pageSize}`,
      headers: {
        "X-CSRFToken": sessionDetails?.csrfToken,
      },
    };
    setLoadingDailyUsage(true);
    axiosPrivate(requestOptions)
      .then((res) => {
        if (res && res.data) {
          const { results, totalCount, page, pageSize, totalPages } = res.data;
          if (results) {
            setDetailedHistory(results);
            setPagination({
              current: page,
              pageSize: pageSize,
              total: totalCount,
              totalPages: totalPages,
            });
          }
        } else {
          throw new Error("Invalid response structure");
        }
      })
      .catch((err) => {
        setAlertDetails(
          handleException(err, "Failed to fetch detailed usage metrics")
        );
      })
      .finally(() => {
        setLoadingDailyUsage(false);
      });
  };
  const dailyUsageEndpoint = `${LLM_WHISPERER_ENDPOINT}/usage/daily/`;

  const getDailyUsageMetrics = (subscriptionId, planName) => {
    setDailyUsage([]);
    const requestOptions = {
      method: "GET",
      url: `${dailyUsageEndpoint}?subscription_id=${subscriptionId}`,
      headers: {
        "X-CSRFToken": sessionDetails?.csrfToken,
      },
    };
    setLoadingDailyUsage(true);
    axiosPrivate(requestOptions)
      .then((res) => {
        const dailyUsageData = res?.data;
        if (dailyUsageData?.usage) {
          setDailyUsage((prevUsage) => [
            ...prevUsage,
            { subscriptionId, planName, usage: dailyUsageData.usage },
          ]);
        }
      })
      .catch((err) => {
        setAlertDetails(
          handleException(err, "Failed to fetch daily usage metrics")
        );
      })
      .finally(() => {
        setLoadingDailyUsage(false);
      });
  };
  const detailedColumns = [
    {
      title: "Document ID",
      dataIndex: "document_id",
      key: "document_id",
    },
    {
      title: "Processing Started At",
      dataIndex: "processing_started_at",
      key: "processing_started_at",
      render: (text) => {
        if (!text) return "-";
        return new Date(text).toLocaleString();
      },
    },
    {
      title: "Processing Time",
      dataIndex: "processing_time",
      key: "processing_time",
    },
    {
      title: "Tag",
      dataIndex: "tag",
      key: "tag",
    },
    {
      title: "Service Type",
      dataIndex: "service_type",
      key: "service_type",
    },
    {
      title: "Processed Page Count",
      dataIndex: "processed_page_count",
      key: "processed_page_count",
    },
    {
      title: "Requested Page Count",
      dataIndex: "requested_page_count",
      key: "requested_page_count",
    },
    {
      title: "Total Page Count",
      dataIndex: "total_page_count",
      key: "total_page_count",
    },
    {
      title: "Upload File Size",
      dataIndex: "upload_file_size",
      key: "upload_file_size",
    },
    {
      title: "Average Page Processing Time",
      dataIndex: "avg_page_processing_time",
      key: "avg_page_processing_time",
    },
    {
      title: "Completed At",
      dataIndex: "completed_at",
      key: "completed_at",
    },
  ];
  const columns = [
    {
      title: "Native Text",
      dataIndex: "nonOcrPageCount",
      key: "nonOcrPageCount",
    },
    {
      title: "Low Cost",
      dataIndex: "lowCostOcrPageCount",
      key: "lowCostOcrPageCount",
    },
    {
      title: "High Quality",
      dataIndex: "highCostOcrPageCount",
      key: "highCostOcrPageCount",
    },
    {
      title: "High Quality with Form Elements",
      dataIndex: "highCostOcrAdvancedPageCount",
      key: "highCostOcrAdvancedPageCount",
    },
    {
      title: "Date",
      dataIndex: "date",
      key: "date",
      filterDropdown: ({ setSelectedKeys, confirm }) => (
        <div style={{ padding: 8 }}>
          <RangePicker
            onChange={(dates) => {
              setSelectedKeys(dates ? [dates] : []);
              handleDateRangeChange(dates);
            }}
            style={{ marginBottom: 8, display: "block" }}
          />
          <Space>
            <Button
              type="primary"
              onClick={() => confirm()}
              size="small"
              style={{ width: 90 }}
            >
              Filter
            </Button>
            <Button
              onClick={() => {
                setSelectedKeys([]);
                handleDateRangeChange(null);
                confirm();
              }}
              size="small"
              style={{ width: 90 }}
            >
              Reset
            </Button>
          </Space>
        </div>
      ),
      onFilter: (value, record) => {
        const [start, end] = value;
        return isDateWithinRange(record?.date, start, end);
      },
    },
  ];

  const getUsageData = () => {
    getOverallUsageMetrics();
    const subscriptionId = getSubscriptionIdByProduct(toggleValue);
    getDailyUsageMetrics(subscriptionId, toggleValue);
  };

  const isDateWithinRange = (date, start, end) => {
    const parsedDate = new Date(date);
    return parsedDate >= start && parsedDate <= end;
  };

  const handleDateRangeChange = (dates) => {
    if (dates) {
      const [start, end] = dates;
      const startDate = new Date(start); // Convert to JavaScript Date
      const endDate = new Date(end); // Convert to JavaScript Date
      startDate.setHours(0, 0, 0, 0); // Start of the day
      endDate.setHours(23, 59, 59, 999); // End of the day

      const filtered = tableData.filter((item) => {
        const itemDate = new Date(item?.date); // Ensure item.date is a JavaScript Date
        return isDateWithinRange(itemDate, startDate, endDate);
      });
      setFilteredData(filtered);
    } else {
      setFilteredData(tableData); // Reset to all data if no range is selected
    }
  };

  const getSubscriptionIdByProduct = (product) => {
    if (usage && usage.length > 0) {
      const plan = usage.find((plan) => plan?.product === product);
      return plan ? plan?.subscription_id : null;
    }
    return null;
  };

  useEffect(() => {
    getOverallUsageMetrics();
  }, []);

  useEffect(() => {
    if (!toggleValue) {
      return;
    }
    const subscriptionId = getSubscriptionIdByProduct(toggleValue);
    getDailyUsageMetrics(subscriptionId, toggleValue);
  }, [toggleValue]);

  useEffect(() => {
    const formattedData = dailyUsage.flatMap((item) =>
      item?.usage?.map((usageItem, index) => ({
        key: `${item.subscriptionId}-${index}`,
        planName: item?.planName,
        date: usageItem?.date,
        nonOcrPageCount: usageItem?.usage?.non_ocr_page_count,
        lowCostOcrPageCount: usageItem?.usage?.low_cost_ocr_page_count,
        highCostOcrPageCount: usageItem?.usage?.high_cost_ocr_page_count,
        highCostOcrAdvancedPageCount:
          usageItem?.usage?.high_cost_ocr_advanced_page_count,
      }))
    );
    setTableData(formattedData);
    setFilteredData(formattedData);
  }, [dailyUsage]);

  return (
    <>
      <TopBar title="Dashboard" enableSearch={false}>
        <SupportComponent />
      </TopBar>
      <div className="user-bg-col">
        <IslandLayout>
          {loadingUsage ? (
            <div>
              <SpinnerLoader />
            </div>
          ) : (
            <>
              <FloatButton
                className="dash-refresh-button"
                onClick={getUsageData}
                icon={<ReloadOutlined />}
              />
              <Tabs defaultActiveKey="usage" className="usage-tabs">
                <TabPane tab="Usage History" key="usage">
                  <h3 className="usage-title">Overall Usage</h3>
                  <Row className="usage-container" gutter={[16, 16]}>
                    {usage?.map((item, index) => (
                      <Col key={index} xs={{ span: 6 }} lg={{ span: 8 }}>
                        <Alert
                          message={
                            <strong style={{ margin: "auto" }}>
                              {item?.product}
                            </strong>
                          }
                          description={
                            !item.usage ? (
                              <strong>No Usage metrics available</strong>
                            ) : (
                              <div>
                                <div className="usage-row">
                                  <strong className="process-type-title">
                                    Native Text
                                  </strong>
                                  :
                                  <span className="process-type-value">
                                    {`${
                                      item?.usage?.non_ocr_page_count || 0
                                    } pages`}
                                  </span>
                                </div>
                                <div className="usage-row">
                                  <strong className="process-type-title">
                                    Low Cost
                                  </strong>
                                  :
                                  <span className="process-type-value">
                                    {`${
                                      item?.usage?.low_cost_ocr_page_count || 0
                                    } pages`}
                                  </span>
                                </div>
                                <div className="usage-row">
                                  <strong className="process-type-title">
                                    High Quality
                                  </strong>
                                  :
                                  <span className="process-type-value">
                                    {`${
                                      item?.usage?.high_cost_ocr_page_count || 0
                                    } pages`}
                                  </span>
                                </div>
                                <div className="usage-row">
                                  <strong className="process-type-title">
                                    High Quality with Form Elements
                                  </strong>
                                  :
                                  <span className="process-type-value">
                                    {`${
                                      item?.usage
                                        ?.high_cost_ocr_advanced_page_count || 0
                                    } pages`}
                                  </span>
                                </div>
                              </div>
                            )
                          }
                          type="info"
                        />
                      </Col>
                    ))}
                  </Row>
                  <div className="user-table">
                    <Flex justify="space-between" align="center">
                      <h3>Usage History</h3>
                      {usage.length > 1 && (
                        <Segmented
                          options={["LLM Whisperer Free", "LLM Whisperer Pro"]}
                          value={toggleValue}
                          onChange={(value) => setToggleValue(value)}
                        />
                      )}
                    </Flex>
                    <Table
                      dataSource={filteredData}
                      columns={columns}
                      pagination={true}
                      showSorterTooltip={{ target: "sorter-icon" }}
                      size="small"
                      loading={isLoadingDailyUsage}
                    />
                  </div>
                </TabPane>
                <TabPane tab="Detailed History" key="detailed">
                  <div className="user-table">
                    <Flex justify="space-between" align="center">
                      <h3>Detailed History</h3>
                      {usage.length > 1 && (
                        <Segmented
                          options={["LLM Whisperer Free", "LLM Whisperer Pro"]}
                          value={toggleValue}
                          onChange={(value) => setToggleValue(value)}
                        />
                      )}
                      <Space>
                        <RangePicker
                          presets={rangePresets}
                          onChange={(dates) => {
                            if (dates) {
                              setDatesDetailedHistory(dates);
                            }
                          }}
                        />
                        <Button
                          type="primary"
                          onClick={() => {
                            getDetailedUsage(1, pagination.pageSize);
                          }}
                        >
                          Get Data
                        </Button>
                      </Space>
                    </Flex>
                    <Table
                      className="detailed-table"
                      dataSource={detailedHistory}
                      pagination={{
                        current: pagination.current,
                        pageSize: pagination.pageSize,
                        total: pagination.total,
                        onChange: (page, pageSize) =>
                          getDetailedUsage(page, pageSize),
                        showSizeChanger: true,
                        showTotal: (total, range) =>
                          `${range[0]}-${range[1]} of ${total} items`,
                      }}
                      columns={detailedColumns}
                      size="small"
                      loading={isLoadingDailyUsage}
                      scroll={{ y: "calc(60vh - 120px)" }}
                      sticky
                    />
                  </div>
                </TabPane>
              </Tabs>
            </>
          )}
        </IslandLayout>
      </div>
    </>
  );
}

export { Dashboard };
